import { useEffect, useState } from "react";
import { admin } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const EMPTY = { name: "", compcode: "", pcode: "", purc_types: [], active: true };

export function AutoApproveRuleDialog({ open, onOpenChange, existing, plants, purcTypeOptions, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!existing?.id;

  useEffect(() => {
    if (open) setForm(existing?.id ? { ...EMPTY, ...existing } : EMPTY);
  }, [open, existing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleType = (t) =>
    set("purc_types", form.purc_types.includes(t) ? form.purc_types.filter((x) => x !== t) : [...form.purc_types, t]);
  const typeOptions = Array.from(new Set([...(purcTypeOptions || []), ...form.purc_types]));

  const submit = async () => {
    const payload = {
      name: form.name, compcode: form.compcode, pcode: form.pcode,
      purc_types: form.purc_types, active: form.active,
    };
    setSaving(true);
    try {
      if (isEdit) await admin.updateAutoRule(existing.id, payload);
      else await admin.createAutoRule(payload);
      toast.success(isEdit ? "Rule updated" : "Rule created");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="auto-rule-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit auto-approve rule" : "New auto-approve rule"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Rule name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={60}
              placeholder="e.g. P4→P2 (Goa → Aligarh Plant 2)" data-testid="auto-rule-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Plant (Compcode)</Label>
              <select value={form.compcode} onChange={(e) => set("compcode", e.target.value)}
                className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white"
                data-testid="auto-rule-plant-select">
                <option value="">Select plant…</option>
                {(plants || []).map((p) => (
                  <option key={p.code} value={p.code}>{p.code} — {p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Supplier code (PCode)</Label>
              <Input value={form.pcode} onChange={(e) => set("pcode", e.target.value.toUpperCase())}
                placeholder="e.g. R1766" data-testid="auto-rule-pcode-input" />
            </div>
          </div>
          <div>
            <Label>Purchase types</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {typeOptions.map((t) => (
                <button key={t} type="button" onClick={() => toggleType(t)} data-testid={`auto-rule-type-${t}`}
                  className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${form.purc_types.includes(t) ? "bg-cyan-700 text-white border-cyan-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="auto-rule-active-switch" />
            <span className="text-sm text-slate-700">{form.active ? "Active" : "Inactive"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="auto-rule-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-cyan-700 hover:bg-cyan-800" data-testid="auto-rule-save-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-1" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
